import React, {
  ReactElement, useCallback, useEffect, useRef, useState
} from 'react';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';
import { useDispatch } from 'react-redux';
import Button from '@/shared/molecules/Button';
import useLiveAgentStyles from './styles';
import { errorModalConfig } from '../common/ChatLiveAgentConfig';
import { openModal } from '@/localization/actions';

interface ContactLiveAgentButtonProps {
  isAgentAvailable: boolean;
}

const ContactLiveAgentButton = ({ isAgentAvailable }: ContactLiveAgentButtonProps): ReactElement => {
  const styles = useLiveAgentStyles();
  const dispatch = useDispatch();
  const chatStatusInterval = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getLiveAgentText = useCallback(() => {
    let liveAgentText = 'Loading...';
    if (!isLoading) {
      liveAgentText = isAgentAvailable ? 'Chat with us' : 'Chat is Offline';
    }
    return liveAgentText;
  }, [isAgentAvailable, isLoading]);

  const showErrorModal = useCallback(() => {
    const modalContent: ModalContent = errorModalConfig;
    dispatch(openModal(modalContent));
  }, [dispatch]);

  const chatStatus = () => {
    let chatStatusIntervalCounter = 0;
    chatStatusInterval.current = setInterval(() => {
      const intervalCheck = chatStatusIntervalCounter > 35;
      const chatWindowDom = document.querySelector('.layout-docked.embeddedServiceSidebar');

      if (chatWindowDom || intervalCheck) {
        setIsLoading(false);
      }

      if (chatStatusInterval.current && (chatWindowDom || intervalCheck)) {
        clearInterval(chatStatusInterval.current);
      }

      if (!chatWindowDom && intervalCheck) {
        showErrorModal();
      }
      chatStatusIntervalCounter += 1;
    }, 1000);
  };

  const onLiveAgentBtnClick = () => {
    if (!document.querySelector('.embeddedServiceSidebar')) {
      setIsLoading(true);
      window?.embedded_svc?.bootstrapEmbeddedService?.();
      chatStatus();
    }
  };

  useEffect(() => () => {
    if (chatStatusInterval.current) {
      clearInterval(chatStatusInterval.current);
    }
  }, []);

  return (
    <div data-testid="contact-live-agent-button-wrapper">
      <Button
        id="liveAgentButton"
        type="button"
        size="large"
        variantStyle="contained"
        prefixIcon={<Image alt="Chat with Us" src="/images/icons/icon-chat-white.svg" height={22} width={22} />}
        label={getLiveAgentText()}
        disabled={!isAgentAvailable}
        fullWidth
        onClick={onLiveAgentBtnClick}
      />
      <Box marginTop=".5rem">
        <Typography data-testid="hour-text" gutterBottom>
          <Typography component="span" className={styles?.bold}>
            Chat hours:
          </Typography>{' '}
          1pm - 12 am CST
        </Typography>
      </Box>
    </div>
  );
};

export default ContactLiveAgentButton;
